/**
 * Pure label-placement geometry for the shared BoundingBoxOverlay (AKL-705).
 * Kept separate from the component so the edge-anchoring rules that decide
 * whether a label stays readable are unit-testable.
 *
 * All lengths are in the image's *layout* space — the image rendered at scale
 * 1, top-left origin, matching the `renderedLeftPx` / `renderedTopPx` values
 * the overlay already derives per box. Any ancestor zoom is accounted for via
 * `labelScale` (`1 / zoom`) rather than by measuring the transformed geometry.
 */

/**
 * Width a label is assumed to need even when its box is narrower, so a short
 * box near the right edge still anchors right instead of letting a long
 * species name spill past the image.
 */
export const LABEL_MIN_WIDTH_PX = 80;

/** Font size the label renders at relative to its inherited size (`font-size: 0.85em`). */
export const LABEL_FONT_SCALE = 0.85;

/** Offset of an above-the-box label, in the label's own font em units (`top: -1.5em`). */
export const LABEL_OFFSET_EM = 1.5;

/**
 * Convert the font size a label inherits into the vertical offset it renders
 * at, in px.
 *
 * `em` on the label's `top` property resolves against the label's *own*
 * computed font size, which is itself `LABEL_FONT_SCALE` of the inherited
 * size — hence both factors are applied.
 *
 * @param inheritedFontSizePx the computed font size (px) the label inherits.
 * @returns the distance in px between the box's top edge and the label's top.
 */
export const labelOffsetPx = (inheritedFontSizePx: number): number =>
  LABEL_OFFSET_EM * LABEL_FONT_SCALE * inheritedFontSizePx;

export interface LabelAnchorInput {
  /** Box's left edge in image-layout px. */
  boxLeftPx: number;
  /** Box's top edge in image-layout px. */
  boxTopPx: number;
  /** Box's width in image-layout px. */
  boxWidthPx: number;
  /** Rendered image width in the same layout space. */
  imageWidthPx: number;
  /** Label offset in px, from {@link labelOffsetPx}. */
  offsetPx: number;
  /** Counter-scale applied to the label (`1 / zoom`); 1 when unzoomed. */
  labelScale: number;
}

export interface LabelAnchor {
  /** Anchor the label to the box's right corner rather than its left. */
  anchorRight: boolean;
  /** Render the label just inside the box's top edge rather than above it. */
  anchorTop: boolean;
}

/**
 * Decide which corner of a box its label anchors to, so the label stays inside
 * the image on both axes.
 *
 * Horizontally the rule is unchanged from AKL-698: a box whose label would
 * reach past the image's right edge anchors right. Vertically (AKL-705) a box
 * sitting too close to the image's top flips its label to just inside the box,
 * because a label above it would render outside the image and be clipped by the
 * surrounding `overflow-hidden` container.
 *
 * The flip threshold is scaled by `labelScale`: the label is counter-scaled
 * about its anchored edge, so the further the reviewer zooms in the less layout
 * space it consumes above the box, and the fewer boxes need to flip.
 *
 * @param input the box's geometry, the image width, and the label's metrics.
 * @returns the horizontal and vertical anchors, decided independently.
 */
export const resolveLabelAnchor = ({
  boxLeftPx,
  boxTopPx,
  boxWidthPx,
  imageWidthPx,
  offsetPx,
  labelScale,
}: LabelAnchorInput): LabelAnchor => {
  // Zero-width guard only, so a caller that hasn't measured its image yet gets
  // a stable `false` rather than an anchor derived from a meaningless width.
  // BoundingBoxOverlay's 1x1 placeholder never reaches here: it renders boxes
  // only once `imageLoaded` is set, which happens after the real dimensions.
  const anchorRight =
    imageWidthPx > 0 &&
    boxLeftPx + Math.max(boxWidthPx, LABEL_MIN_WIDTH_PX) > imageWidthPx;

  const overhangPx = offsetPx * labelScale;
  const anchorTop = boxTopPx < overhangPx;

  return { anchorRight, anchorTop };
};
